import React, { useEffect, useRef, useState } from "react";
import { Typography, Box } from "@mui/material";
import { useInView } from "framer-motion";

interface StatCounterProps {
  value: number;
  suffix?: string;
  label: string;
}

/**
 * Renders the final value as real text so prerendered/static HTML is always
 * correct; the count-up animation only runs client-side once in view.
 */
const StatCounter: React.FC<StatCounterProps> = ({ value, suffix = "", label }) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [display, setDisplay] = useState(value);
  const animated = useRef(false);

  useEffect(() => {
    // Skip the animation during prerendering so the snapshot holds the real value
    if (!inView || animated.current || (typeof navigator !== "undefined" && navigator.webdriver)) {
      return;
    }
    animated.current = true;
    const duration = 1200;
    const start = performance.now();
    let raf: number;
    const tick = (now: number) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(eased * value));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    setDisplay(0);
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);

  return (
    <Box ref={ref} sx={{ textAlign: "center" }}>
      <Typography
        variant="h2"
        component="p"
        sx={{ fontWeight: 800, color: "secondary.light", lineHeight: 1 }}
      >
        {display.toLocaleString("en-IN")}
        {suffix}
      </Typography>
      <Typography variant="body1" sx={{ mt: 1, opacity: 0.85 }}>
        {label}
      </Typography>
    </Box>
  );
};

export default StatCounter;
